import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    CircularProgress,
    Box,
    Typography,
    Grid,
} from '@mui/material';
import { IAgent } from '../../definitions/app/agent.d';
import toast from 'react-hot-toast';
import { addAgent } from '../../app/services/agents/agentAPI';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormProvider } from '../hook-form';

interface AddAgentModalProps {
    open: boolean;
    onClose: () => void;
    onAddAgent: (agentData: IAgent) => void;
}

const AddAgentModal: React.FC<AddAgentModalProps> = ({ open, onClose, onAddAgent }) => {
    const [loading, setLoading] = useState(false);

    const AddAgentSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
        mobile: Yup.string().matches(/^[0-9]{10}$/, 'Mobile number is not valid').required('Mobile number is required'),
        address: Yup.string().required('Address is required'),
        active: Yup.boolean().required('Active status is required'),
        fare: Yup.number().typeError('Fare must be a number').required('Fare is required').min(0, 'Fare cannot be negative'),
        kg14_refill: Yup.number().typeError('14KG Refilling must be a number').required('14KG Refilling is required').min(0, '14KG Refilling cannot be negative'),
        kg14_nc: Yup.number().typeError('14KG NC must be a number').required('14KG NC is required').min(0, '14KG NC cannot be negative'),
        kg14_dbc: Yup.number().typeError('14KG DBC must be a number').required('14KG DBC is required').min(0, '14KG DBC cannot be negative'),
        kg19_refill: Yup.number().typeError('19KG Refilling must be a number').required('19KG Refilling is required').min(0, '19KG Refilling cannot be negative'),
        kg19_nc: Yup.number().typeError('19KG NC must be a number').required('19KG NC is required').min(0, '19KG NC cannot be negative'),
        kg5_ftl_refill: Yup.number().typeError('5KG FTL Refilling must be a number').required('5KG FTL Refilling is required').min(0, '5KG FTL Refilling cannot be negative'),
        kg5_ftl_nc: Yup.number().typeError('5KG FTL NC must be a number').required('5KG FTL NC is required').min(0, '5KG FTL NC cannot be negative'),
        kg5_domestic: Yup.number().typeError('5KG Domestic must be a number').required('5KG Domestic is required').min(0, '5KG Domestic cannot be negative'),


    });

    const defaultValues: IAgent = {
        name: '',
        mobile: '',
        address: '',
        active: true,
        fare: 0,
        kg5_ftl_refill: 0,
        kg5_ftl_nc: 0,
        kg5_domestic: 0,
        kg14_refill: 0,
        kg14_nc: 0,
        kg14_dbc: 0,
        kg19_refill: 0,
        kg19_nc: 0,
    };

    const methods = useForm<IAgent>({
        resolver: yupResolver(AddAgentSchema),
        defaultValues,
        mode: 'onTouched',
    });

    const { handleSubmit, reset, control } = methods;

    useEffect(() => {
        if (!open) {
            reset(defaultValues);
        }
    }, [open, reset]);

    const onSubmit = handleSubmit(async (formData) => {
        try {
            setLoading(true);
            const payload = {
                name: formData.name,
                mobile: formData.mobile,
                address: formData.address,
                active: formData.active,
                fare: formData.fare,
                kg14_refill: formData.kg14_refill,
                kg14_nc: formData.kg14_nc,
                kg14_dbc: formData.kg14_dbc,
                kg19_refill: formData.kg19_refill,
                kg19_nc: formData.kg19_nc,
                kg5_ftl_refill: formData.kg5_ftl_refill,
                kg5_ftl_nc: formData.kg5_ftl_nc,
                kg5_domestic: formData.kg5_domestic,
            };
            const result = await addAgent(payload);
            if (result.status === "success") {
                setLoading(false);
                toast.success("Agent added successfully");
                onClose();
            } else {
                toast.error(result.message || 'Failed to add agent');
            }
            setLoading(false);
        } catch (error) {
            //@ts-ignore
            toast.error(error.response?.data?.message || 'Failed to add agent ');
        }
    });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add New Agent</DialogTitle>
            <DialogContent>
                <FormProvider methods={methods} onSubmit={onSubmit}>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Agent Name"
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="mobile"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Mobile Number"
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Address"
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="fare"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Fare"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="active"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        label="Status"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={String(field.value)}
                                        onChange={(e) => field.onChange(e.target.value === 'true')}
                                    >
                                        <MenuItem value="true">Active</MenuItem>
                                        <MenuItem value="false">Inactive</MenuItem>
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg14_refill"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="14KG Refill Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg14_nc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="14KG NC Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg14_dbc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="14KG DBC Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg19_refill"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="19KG Refill Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg19_nc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="19KG NC Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg5_ftl_refill"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="5KG FTL Refill Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg5_ftl_nc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="5KG FTL NC Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg5_domestic"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="5KG Domestic Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value === 0 ? '' : field.value}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                {loading ? (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <CircularProgress sx={{ color: '#1976d2' }} />
                        <Typography variant="body1" color="textSecondary">
                            Loading data...
                        </Typography>
                    </Box>
                ) : (
                    <Button onClick={onSubmit} variant="contained">
                        Add Agent
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AddAgentModal;